import React, { useEffect, useState } from "react";
import { DateTime } from "luxon";
import { SmartTextField } from "../../core/SmartTextField";
import { FullScreenDialog } from "../../core/FullScreenDialog";
import Alert from "@mui/material/Alert";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardActions from "@mui/material/CardActions";
import CardContent from "@mui/material/CardContent";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemSecondaryAction from "@mui/material/ListItemSecondaryAction";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Heading } from "../../core/Heading";
import { enqueueSnackbar } from "notistack";
import { useFindByEmail, useInviteUser, useUserUsers, useGame, useContact } from "@seabrookstudios/pitch2table";
import { formatContactLocation, formatFullName } from "@seabrookstudios/pitch2table-core";
import { CircularProgressSize } from "../../theme";
import CardHeader from "@mui/material/CardHeader";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import { formatTimeZone } from "../../core/date-formatting";

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 * @param {any} props.contact
 * @param {string} [props.email]
 */
const UserCard = ({ gameId, contact, email }) => {
  const onAddUserSuccess = () => {
    enqueueSnackbar("Invite Sent", { variant: "success" });
  };

  const onAddUserError = () => {
    enqueueSnackbar("Failed to invite co-designer. Please try again.", { variant: "error" });
  };

  const inviteUser = useInviteUser(gameId, onAddUserSuccess, onAddUserError);

  const addUserHandler = async () => {
    await inviteUser.mutateAsync({ userId: contact.id });
  };

  const contactTime = DateTime.now().setZone(contact.location.timezone);
  const text = "Invite Designer";

  return (
    <Card elevation={2}>
      <Box p={3}>
        <Stack direction="row" display="flex">
          <Stack flex={1} justifyContent="center" alignItems="center">
            <Avatar src={contact.pictureUrl} style={{ width: 128, height: 128 }} />
          </Stack>
          <Stack flex={2}>
            <Heading variant="h5">{formatFullName(contact)}</Heading>
            <Typography pb={1}>{contact.pronouns}</Typography>
            <Divider />
            <Box>
              <Stack direction="column" display="flex">
                <Stack direction="row">
                  <Stack flex={1} justifyContent="center">
                    <Typography variant="body2" textTransform="uppercase" color="text.secondary">
                      Preferred Name
                    </Typography>
                  </Stack>
                  <Stack flex={1} justifyContent="center">
                    <Typography>{contact.preferredName || "Unknown"}</Typography>
                  </Stack>
                </Stack>
                {email && (
                  <Stack direction="row">
                    <Stack flex={1} justifyContent="center">
                      <Typography variant="body2" textTransform="uppercase" color="text.secondary">
                        Email
                      </Typography>
                    </Stack>
                    <Stack flex={1} justifyContent="center">
                      <Typography>{email}</Typography>
                    </Stack>
                  </Stack>
                )}
                <Stack direction="row">
                  <Stack flex={1} justifyContent="center">
                    <Typography variant="body2" textTransform="uppercase" color="text.secondary">
                      Location
                    </Typography>
                  </Stack>
                  <Stack flex={1} justifyContent="center">
                    <Typography>{formatContactLocation(contact.location) || "Unknown"}</Typography>
                  </Stack>
                </Stack>
                <Stack direction="row">
                  <Stack flex={1} justifyContent="center">
                    <Typography variant="body2" textTransform="uppercase" color="text.secondary">
                      Time Zone
                    </Typography>
                  </Stack>
                  <Stack flex={1} justifyContent="center">
                    <Typography>{contact.location.timezone || "Unknown"}</Typography>
                  </Stack>
                </Stack>
                <Stack direction="row">
                  <Stack flex={1} justifyContent="center">
                    <Typography variant="body2" textTransform="uppercase" color="text.secondary">
                      Local Time
                    </Typography>
                  </Stack>
                  <Stack flex={1} justifyContent="center">
                    <Typography>
                      {contact.location.timezone ? contactTime.toLocaleString(DateTime.DATETIME_FULL) : "Unknown"}
                    </Typography>
                  </Stack>
                </Stack>
              </Stack>
            </Box>
          </Stack>
        </Stack>
      </Box>
      <CardActions>
        <Button onClick={addUserHandler}>
          {inviteUser.isPending ? <CircularProgress size={CircularProgressSize} /> : text}
        </Button>
      </CardActions>
    </Card>
  );
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 * @param {import("@seabrookstudios/types").UserId[]} props.historicalUserIds
 */
const HistoricalInvites = ({ gameId, historicalUserIds = [] }) => {
  if (historicalUserIds.length === 0) {
    return null;
  }

  return (
    <>
      <CardHeader title="Prior Co-designers" />
      <List>
        {historicalUserIds.map((userId) => (
          <HistoricalUserCard key={userId.toString()} gameId={gameId} userId={userId} />
        ))}
      </List>
    </>
  );
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 * @param {any} props.userId
 */
const HistoricalUserCard = ({ gameId, userId }) => {
  const { data: contact, isFetching } = useContact(/** @type {import("@seabrookstudios/types").UserId} */ (userId));

  const onAddUserSuccess = () => {
    enqueueSnackbar("Invite Sent", { variant: "success" });
  };

  const onAddUserError = () => {
    enqueueSnackbar("Failed to invite co-designer. Please try again.", { variant: "error" });
  };

  const inviteUser = useInviteUser(gameId, onAddUserSuccess, onAddUserError);

  const addUserHandler = async () => {
    if (!contact) {
      return;
    }
    await inviteUser.mutateAsync({ userId: contact.id });
  };

  if (isFetching) {
    return (
      <ListItem>
        <Box width="100%" display="flex" justifyContent="center" alignItems="center" py={2}>
          <CircularProgress />
        </Box>
      </ListItem>
    );
  }

  if (!contact) {
    return null;
  }

  const secondaryText = `${formatContactLocation(contact.location)} · ${formatTimeZone(contact.location.timezone)}`;
  const primaryText = formatFullName(contact);
  const inviteText = "Invite";

  return (
    <ListItem>
      <ListItemAvatar>
        <Avatar src={contact.pictureUrl} />
      </ListItemAvatar>
      <ListItemText primary={primaryText} secondary={secondaryText} />
      <ListItemSecondaryAction>
        <Button onClick={addUserHandler} variant="outlined">
          {inviteUser.isPending ? <CircularProgress size={CircularProgressSize} /> : inviteText}
        </Button>
      </ListItemSecondaryAction>
    </ListItem>
  );
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 * @param {string} props.email
 */
const SearchResults = ({ gameId, email }) => {
  const { data: contact, isFetching } = useFindByEmail(email);

  if (!email) {
    return null;
  }

  if (isFetching) {
    return (
      <Card>
        <Box p={3} display="flex" justifyContent="center" alignItems="center">
          <CircularProgress />
        </Box>
      </Card>
    );
  }

  if (!contact) {
    return (
      <Card>
        <Box p={3}>
          <Typography>No results found for &quot;{email}&quot;</Typography>
        </Box>
      </Card>
    );
  }

  return <UserCard gameId={gameId} contact={contact} email={email} />;
};

/**
 * @param {object} props
 * @param {boolean} props.disabled
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 */
export const InviteDesignerToGame = ({ disabled, gameId }) => {
  const { data: game } = useGame(gameId);
  const { data: allUserUsers = [] } = useUserUsers();

  const [text, setText] = useState("");
  /**
   * @type {[(string|null),import("react").Dispatch<import("react").SetStateAction<(string|null)>>]}
   */
  const [email, setEmail] = useState(null);
  const [searchDisabled, setSearchDisabled] = useState(true);

  const historicalUserIds = allUserUsers.filter((userId) => {
    if (!game) {
      return true;
    }

    return !game.designers.some((designer) => designer.designerId === userId);
  });

  const searchForUser = async () => {
    setEmail(text);
  };

  useEffect(() => {
    setSearchDisabled(text.length === 0 || !text.includes("@"));
  }, [text]);

  return (
    <FullScreenDialog disabled={disabled} title="Invite Co-designer">
      <Card elevation={2}>
        <CardContent>
          <Alert severity="warning">
            Your co-designer will have the same access as you to this game. Make sure you are inviting the correct
            person.
          </Alert>
          <Stack direction="row">
            <SmartTextField
              required
              label="Email Address"
              helperText="Look up the email address of an existing user. Exact match only."
              onChange={setText}
              onEnter={searchForUser}
              onEnterDisabled={searchDisabled}
            />
            <Box display="flex" justifyContent="center" alignItems="center" p={1}>
              <Button variant="contained" onClick={searchForUser} disabled={searchDisabled}>
                SEARCH
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>
      <Box p={1} />
      <SearchResults gameId={gameId} email={email || ""} />
      <HistoricalInvites gameId={gameId} historicalUserIds={historicalUserIds} />
    </FullScreenDialog>
  );
};
